import React from 'react';
import { isEmpty } from 'lodash';
import { useMediaQuery } from 'react-responsive';
import { theme } from '@fiverr-private/theme';
import { getContext } from '@fiverr-private/fiverr_context';
import { Container } from '@fiverr-private/layout_components';
import { CollectionMenuOption, ResourceType } from '../../types';
import { useResourceCollections } from '../../hooks/useResourceCollections';
import { RenderTriggerProps } from '../SaveToListButton/SaveToListButton';
import { sendClickOnCollectActionsMenuEvent } from '../../utils/events/freelancersListEvents';
import { CollectionsComponentProps } from '../../context/CollectionsContext';
import { ButtonProps } from './SaveToListPopover';
import { SaveToListDrawer } from './SaveToListDrawer/SaveToListDrawer';
import { SaveToListDropdown } from './SaveToListDropdown/SaveToListDropdown';
import { requireSignIn } from './requireSignIn';

interface SaveToListViewProps {
    resourceType: ResourceType;
    resourceId: string;
    isOpen: boolean;
    onChange: (collection: CollectionMenuOption) => void;
    onOpenChange: (isOpen: boolean) => void;
    onOpenEditModal: () => void;
    renderTrigger: (props: RenderTriggerProps) => React.ReactElement<ButtonProps>;
    currentCollectionId?: string;
    fullWidth?: boolean;
    position?: React.ComponentProps<typeof import('@fiverr-private/dropdown').Dropdown>['position'];
    bi: NonNullable<CollectionsComponentProps['bi']>;
    triggerData?: {
        triggerType?: string;
        triggerCopy?: string;
        triggerPlacement?: string;
    };
}

export const SaveToListView: React.FC<SaveToListViewProps> = ({
    resourceType,
    resourceId,
    isOpen,
    onChange,
    onOpenChange,
    onOpenEditModal,
    renderTrigger,
    currentCollectionId,
    fullWidth,
    position,
    bi,
    triggerData = {},
}) => {
    const { isMobile } = getContext();
    const { collectionOptions, inCollections, isLoading } = useResourceCollections({
        resourceType,
        resourceId,
        currentCollectionId,
    });
    const isSmall = useMediaQuery({ query: theme.breakpointsReverse.sm });
    const showDrawer = isMobile || isSmall;
    const hasMenuOptions = !isEmpty(collectionOptions);
    const isMenuOpen = isOpen && hasMenuOptions;

    const protectedOpen = () => {
        requireSignIn({
            pageName: bi.source,
            triggerData,
            onSuccess: () => {
                onOpenChange(true);
                sendClickOnCollectActionsMenuEvent({ biContext: bi });
            },
        });
    };

    const handleOpenChange = (nextIsOpen: boolean) => {
        if (nextIsOpen) {
            protectedOpen();
        } else {
            onOpenChange(false);
        }
    };

    const trigger = renderTrigger({ inCollections, isLoading: isLoading && isOpen });

    if (!hasMenuOptions) {
        return (
            <Container display="inlineFlex" width={fullWidth ? '100%' : undefined} onClick={protectedOpen}>
                {trigger}
            </Container>
        );
    }

    if (showDrawer) {
        return (
            <SaveToListDrawer
                collections={collectionOptions}
                trigger={trigger}
                onChange={onChange}
                isOpen={isMenuOpen}
                onOpenChange={handleOpenChange}
                onClickCreateList={onOpenEditModal}
            />
        );
    }

    return (
        <SaveToListDropdown
            collections={collectionOptions}
            trigger={trigger}
            onChange={onChange}
            isOpen={isMenuOpen}
            onOpenChange={handleOpenChange}
            onClickCreateList={onOpenEditModal}
            fullWidth={fullWidth}
            position={position}
        />
    );
};
